import { Constants } from "./constants";

/**
 * Maps each show_type to the nested score field(s) that hold its judged
 * result_type (see IScore in @eagerdog/interfaces). Used only by
 * normalizeScoreForExport() below. Agility maps to both fields since AKC
 * events store results on `akc_agility` while every other club uses
 * `agility`. Show types with no nested result object (e.g. Conformation,
 * which isn't Q/NQ scored) are intentionally omitted - normalization is a
 * no-op for them.
 */
const SHOW_TYPE_RESULT_FIELDS: Record<string, string[]> = {
  [Constants.show_type.herding]: ["herding"],
  [Constants.show_type.pre_trial_herding]: ["herding"],
  [Constants.show_type.instinct_test_herding]: ["herding"],
  [Constants.show_type.obedience]: ["obedience"],
  [Constants.show_type.rally_obedience]: ["rally_obedience"],
  [Constants.show_type.agility]: ["agility", "akc_agility"],
  [Constants.show_type.nosework]: ["nosework"],
  [Constants.show_type.scentwork]: ["scentwork", "scentwork_master"],
  [Constants.show_type.scent]: ["scent"],
  [Constants.show_type.fast_cat]: ["fastcat"],
  [Constants.show_type.fast_dash]: ["fastdash"],
  [Constants.show_type.cat]: ["cat"],
  [Constants.show_type.fetch]: ["fetch"],
  [Constants.show_type.urban_rat_race]: ["urban_rat_race"],
  [Constants.show_type.country_rat_race]: ["country_rat_race"],
  [Constants.show_type.lost_and_found]: ["lost_and_found"],
  [Constants.show_type.dock_jumping]: ["dock_jumping"],
  [Constants.show_type.dock_diving]: ["dock_jumping"],
  [Constants.show_type.shed_dog]: ["shed_dog"],
  [Constants.show_type.precision_coursing]: ["precision_coursing"],
  [Constants.show_type.weight_pull]: ["weight_pull"],
  [Constants.show_type.farm_dog]: ["farm_dog"],
  [Constants.show_type.water_dog]: ["water_dog"],
  [Constants.show_type.barn_hunt]: ["barn_hunt"],
  [Constants.show_type.scent_dog_games]: ["scent_dog_games"],
  [Constants.show_type.cdsp_video]: ["cdsp"],
};

/** Nested fields that are arrays (one entry per element/leg) rather than a single object. */
const ARRAY_RESULT_FIELDS = new Set<string>(["scentwork_master"]);

/**
 * Print/export-only rule: a dog that was Scratched or flagged as a Conflict
 * never actually ran, so every generated artifact (catalogs, scoresheets,
 * judge books, champion/high-in-trial/secretary-certification reports, and
 * AKC/UKC electronic results uploads) should show it exactly like a
 * judge-entered Absent ("ABS") - never blank, and never a stale Q/NQ/etc.
 * left over from before it was scratched/flagged.
 *
 * This intentionally does NOT change score.status or any front-end display -
 * the web Results grid and mobile Entries/Scoring screens keep reading
 * score.status directly (via @eagerdog/scoring-core's getResultType()) and
 * keep showing "Scratched"/"Conflict" exactly as today. This helper is only
 * ever applied to an in-memory copy right before scores are handed to a
 * print/export builder - it never mutates the input and never touches the DB.
 */
export function normalizeScoreForExport<
  T extends { status?: string; show_type?: string }
>(score: T): T {
  const isScratchedOrConflict =
    score.status === Constants.score_status.scratched ||
    score.status === Constants.score_status.conflict;

  if (!isScratchedOrConflict) {
    return score;
  }

  const fields = SHOW_TYPE_RESULT_FIELDS[score.show_type || ""] || [];
  if (fields.length === 0) {
    return score;
  }

  // score is frequently a live Mongoose document, not a plain object - its
  // real field values live behind prototype getters (own enumerable keys are
  // just $__/$isNew/_doc), so a naive `{ ...score }` silently drops every
  // field except the one we patch below. Convert to a plain object first.
  const plainScore: any =
    typeof (score as any).toObject === "function"
      ? (score as any).toObject()
      : score;
  const patched: any = { ...plainScore };

  for (const field of fields) {
    const existing = (score as any)[field];

    // Array-typed nested fields (e.g. scentwork_master, one entry per
    // element) - only normalize elements that already exist; there's
    // nothing to synthesize (or a shape to guess) if the array was never
    // populated.
    if (Array.isArray(existing)) {
      if (existing.length > 0) {
        patched[field] = existing.map(() => ({
          result_type: Constants.score_result_type.absent,
        }));
      }
      continue;
    }
    if (ARRAY_RESULT_FIELDS.has(field)) {
      continue;
    }

    // Replace (not merge) so any stale judged data (time/score/faults/etc.
    // entered before the dog was scratched/flagged) never leaks into the
    // export alongside the ABS label.
    patched[field] = { result_type: Constants.score_result_type.absent };
  }

  return patched;
}

/** Convenience wrapper for normalizeScoreForExport() over an array of scores. */
export function normalizeScoresForExport<
  T extends { status?: string; show_type?: string }
>(scores: T[]): T[] {
  return scores.map((score) => normalizeScoreForExport(score));
}
